// Login / signup view — WebAuthn passkey flows (A: signup, B: discoverable login,
// C: cross-device join stub). No sidebar; centered card matching login.html mockup.
import { h, toast, icon, brandLogo } from '../components/dom.js';
import { router } from '../router/router.js';
import { api } from '../api/client.js';
import { ApiError } from '../api/http.js';
import { session } from '../api/session.js';
import { createDPoPKeypair } from '../crypto/dpop.js';
import { createPasskey, getPasskeyAssertion, webauthnSupported, platformAuthenticatorAvailable } from '../crypto/webauthn.js';
import { ensureSubscriberKeypair } from '../crypto/splitkey.js';
import { nextAction, tickCountdown, terminalMessage, isTerminal, JOIN_WAIT_SECONDS, JOIN_REDIRECT_SECONDS } from '../lib/join-wait.js';
import { avatar } from '../components/dom.js';
import { listAccounts, rememberAccount, forgetAccount } from '../api/accounts.js';
import { canProceed, normalizeName, runSignupOnboarding } from '../lib/onboarding.js';
import { enrollPasskeyBestEffort, recoveryEnrollBegin } from '../lib/passkey-enroll.js';
import { decideSignupFirst } from '../lib/login-experience.js';

export async function renderLogin(_params?: Record<string, string>, query?: URLSearchParams): Promise<void> {
  const app = document.getElementById('app')!;

  // --- Which experience? ----------------------------------------------------
  // We cannot truly ENUMERATE passkeys for this RP (WebAuthn deliberately hides
  // that for privacy). The best available signal that this device belongs to an
  // EXISTING user is: has this device remembered any account (i.e. signed in here
  // before)? If yes -> keep the current sign-in-first experience. If not -> the
  // user "might not have an account yet", so flip to the signup-first experience.
  //
  // Test hooks (override the heuristic so the flows can be demoed on any device):
  //   ?firstlogin=1   -> FORCE the new-user (signup-first) experience
  //   ?existinguser=1 -> FORCE the current (sign-in-first) experience
  const forceNew = query?.get('firstlogin') === '1';
  const forceExisting = query?.get('existinguser') === '1';
  const signupFirst = decideSignupFirst({
    rememberedAccounts: listAccounts().length,
    forceNew,
    forceExisting,
  });

  const status = h('p', { class: 'muted', style: 'min-height:1.2em' });
  // Toggle red error styling on the status line. setErr() marks it an error (red,
  // role=alert); setInfo() clears back to the neutral muted message. Keeps success/
  // progress text calm while making failures (rate limit, invalid username, etc.)
  // impossible to miss.
  const setErr = (msg: string) => { status.textContent = msg; status.classList.add('status-error'); status.setAttribute('role', 'alert'); };
  const setInfo = (msg: string) => { status.textContent = msg; status.classList.remove('status-error'); status.removeAttribute('role'); };
  const usernameInput = h('input', { class: 'input', id: 'username', placeholder: 'pick-a-handle', autocomplete: 'username' }) as HTMLInputElement;

  // In the signup-first (new-user) experience the Create-account button is the
  // PRIMARY CTA; in the current experience it stays a ghost button (passkey is primary).
  const signupBtn = h('button', { class: `btn btn-block ${signupFirst ? 'btn-primary' : 'btn-ghost'}` }, 'Create account');
  // Primary CTA — the GitHub-style zero-typing passkey sign-in (modal mediation):
  // clicking pops the OS passkey picker listing every credential for this site.
  // In the signup-first experience it's demoted to a small ghost button so
  // "Create account" stays the dominant action; in the current experience it
  // stays the big primary button.
  const passkeyBtn = h('button', { class: signupFirst ? 'btn btn-ghost btn-passkey' : 'btn btn-primary btn-block btn-passkey' },
    icon('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>', 18), 'Sign in with a passkey');

  // Coordinates the background conditional-UI (autofill) attempt so an explicit click can abort it.
  let conditionalAbort: AbortController | null = null;
  const accountsWrap = h('div', {});

  // First-run onboarding: after a successful FIRST-TIME signup (not normal login),
  // show a tiny screen capturing First name (required) + Last name (optional).
  // Submitting populates the user's given_name + family_name fields (already seeded
  // + exposed on the Public profile at registration), so new users don't start with
  // empty names. Resolves once the user proceeds (best-effort — a failed save still
  // lets them into the app). Renders in place of the auth card.
  function showOnboardingName(): Promise<void> {
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => { if (done) return; done = true; resolve(); };

      const first = h('input', { class: 'input', id: 'onb-first', placeholder: 'First name', autocomplete: 'given-name' }) as HTMLInputElement;
      const last = h('input', { class: 'input', id: 'onb-last', placeholder: 'Last name', autocomplete: 'family-name' }) as HTMLInputElement;
      const nextBtn = h('button', { class: 'btn btn-primary btn-block' }, 'Next') as HTMLButtonElement;

      const syncNext = () => {
        if (canProceed(first.value)) nextBtn.removeAttribute('disabled');
        else nextBtn.setAttribute('disabled', 'true');
      };
      first.addEventListener('input', syncNext);
      syncNext();

      let submitting = false;
      const submit = async () => {
        if (submitting || !canProceed(first.value)) return;
        submitting = true;
        nextBtn.setAttribute('disabled', 'true');
        try {
          await api.auth.onboardingSetName(normalizeName(first.value), normalizeName(last.value));
        } catch {
          // Best-effort: never trap the user on this screen.
          toast('Could not save your name — you can add it later.', 'warn');
        }
        finish();
      };
      nextBtn.addEventListener('click', () => { void submit(); });
      const onEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } };
      first.addEventListener('keydown', onEnter);
      last.addEventListener('keydown', onEnter);

      const card = h('div', { class: 'card', style: 'max-width:420px;width:100%' },
        h('div', { class: 'brand', style: 'font-size:1.3rem;margin-bottom:6px' },
          brandLogo(28), 'VirtualID'),
        h('h1', { style: 'font-size:1.5rem' }, 'What should we call you?'),
        h('p', { class: 'muted' }, 'Add your name to your public VirtualID profile. You can change this anytime.'),
        h('div', { class: 'field' },
          h('label', { class: 'label', for: 'onb-first' }, 'First name'),
          first),
        h('div', { class: 'field' },
          h('label', { class: 'label', for: 'onb-last' }, 'Last name'),
          last,
          h('small', { class: 'muted' }, 'Optional, but recommended.')),
        nextBtn,
      );
      app.replaceChildren(h('div', {
        style: 'min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;background:var(--bg-soft)',
      }, card));
      first.focus();
    });
  }

  async function finishSession(): Promise<void> {
    // Ensure a subscriber keypair exists for split-key operations post-login.
    try { await ensureSubscriberKeypair(); } catch { /* IndexedDB may be blocked; non-fatal */ }
    toast('Signed in', 'ok');
    // Resume a pending invite deep-link stashed before the login bounce.
    try {
      const raw = sessionStorage.getItem('vid_pending_invite');
      if (raw) {
        sessionStorage.removeItem('vid_pending_invite');
        const pend = JSON.parse(raw) as { alias?: string; code?: string; token?: string };
        if (pend.alias && pend.code) { router.navigate(`/id/${pend.alias}/invite/${pend.code}?autosubscribe=1`); return; }
        if (pend.token) { router.navigate(`/subscribe?token=${encodeURIComponent(pend.token)}&autosubscribe=1`); return; }
      }
    } catch { /* ignore malformed */ }
    // Resume an authed deep-link (e.g. /contacts/<handle>) stashed by the auth
    // guard before the login bounce, so the user lands where they intended.
    try {
      const back = sessionStorage.getItem('vid_return_to');
      if (back) {
        sessionStorage.removeItem('vid_return_to');
        if (back !== '/' && back !== '/login') { router.navigate(back); return; }
      }
    } catch { /* ignore */ }
    router.navigate('/');
  }

  // Flow A — signup
  signupBtn.addEventListener('click', async () => {
    const username = usernameInput.value.trim();
    if (!username) { setErr('Enter a username.'); return; }
    // Cancel any in-flight conditional-UI login get() first; otherwise the browser
    // rejects the signup create() with "A request is already pending."
    abortConditional();
    signupBtn.setAttribute('disabled', 'true');
    try {
      setInfo('Checking availability…');
      const check = await api.auth.usernameCheck(username);
      if (!check.available) { setErr(`Unavailable: ${check.reason || 'profile already taken'}`); signupBtn.removeAttribute('disabled'); return; }
      setInfo('Available ✓ — create your passkey…');
      await createDPoPKeypair(); // generate DPoP key at ceremony time (AUTH-FLOWS); proof carries the jkt
      const ceremony = await api.auth.registerBegin(username);
      const credential = await createPasskey(ceremony.options, {
        onWaitingForFocus: (waiting) => {
          if (waiting) setInfo('Almost there — tap anywhere on the page to finish creating your passkey…');
          else setInfo('Creating your passkey…');
        },
      });
      const success = await api.auth.registerFinish(ceremony.ceremony_id, credential);
      session.set(success);
      rememberAccount({ username, display: username });
      // First-time signup only: capture first/last name, then ALWAYS finishSession
      // (which resumes any pending invite→subscribe deep-link). Normal login
      // (runPasskeyLogin) intentionally does NOT show this screen.
      await runSignupOnboarding(showOnboardingName, finishSession);
    } catch (e) {
      setErr(errText(e));
      signupBtn.removeAttribute('disabled');
    }
  });

  // Abort any in-flight conditional-UI request so it doesn't compete with an explicit ceremony.
  function abortConditional(): void {
    if (conditionalAbort) { conditionalAbort.abort(); conditionalAbort = null; }
  }

  // Is this a benign user-cancel (dismissed the OS picker)? Then don't show a scary error.
  function isCancel(e: unknown): boolean {
    return e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'AbortError');
  }

  // Run a passkey assertion ceremony for the given handle ('' => discoverable/modal picker),
  // persist the session, remember the account, and navigate. Shared by the passkey button and
  // remembered-account rows.
  async function runPasskeyLogin(handle: string): Promise<void> {
    abortConditional();
    setInfo('Waiting for your passkey…');
    await createDPoPKeypair();
    const ceremony = await api.auth.loginBegin(handle);
    const credential = await getPasskeyAssertion(ceremony.options); // default (modal) mediation
    const success = await api.auth.loginFinish(ceremony.ceremony_id, credential);
    session.set(success);
    // Best-effort: remember whatever display identifier we know (typed handle, else server-provided).
    rememberAccount({
      username: handle || success.username || success.handle,
      handle: success.handle,
      display: success.display_name,
      alias: success.display_name,
    });
    await finishSession();
  }

  // PRIMARY — GitHub-style discoverable sign-in: empty handle => OS shows its native passkey
  // picker listing every credential for this RP. Zero typing required.
  passkeyBtn.addEventListener('click', async () => {
    passkeyBtn.setAttribute('disabled', 'true');
    try {
      await runPasskeyLogin('');
    } catch (e) {
      if (isCancel(e)) setInfo(''); else setErr(errText(e));
      passkeyBtn.removeAttribute('disabled');
    }
  });

  // Render the "Accounts on this device" list above the form. Tapping a row runs a passkey
  // ceremony hinted to that account's credentials (handle-based), falling back to discoverable.
  function renderAccounts(): void {
    const accounts = listAccounts();
    if (!accounts.length || !supported) { accountsWrap.replaceChildren(); return; }
    const rows = accounts.map((acc) => {
      const forget = h('button', { class: 'acct-forget', title: `Forget ${acc.username}`, 'aria-label': `Forget ${acc.username}` }, '✕');
      forget.addEventListener('click', (ev) => {
        ev.stopPropagation();
        // Guard against accidental clicks (the ✕ is small and easy to hit). This only
        // removes the account from THIS device's list — it doesn't delete the account.
        const ok = confirm(`Remove “${acc.username}” from this device?\n\nThis only forgets it from the sign-in list on this device — your account and passkey are not deleted, and you can sign in again anytime.`);
        if (!ok) return;
        forgetAccount(acc.username);
        renderAccounts();
      });
      const row = h('button', { class: 'acct-row' },
        avatar(acc.alias || acc.username, 34),
        h('span', { class: 'acct-name' },
          h('span', { class: 'acct-user' }, acc.username),
          acc.alias && acc.alias !== acc.username ? h('span', { class: 'acct-alias muted' }, acc.alias) : null),
        forget);
      row.addEventListener('click', async () => {
        row.setAttribute('disabled', 'true');
        try {
          // Prefer handle-based ceremony to target this account's credentials; fall back to discoverable.
          try { await runPasskeyLogin(acc.username); }
          catch (e) { if (isCancel(e)) throw e; await runPasskeyLogin(''); }
        } catch (e) {
          if (isCancel(e)) setInfo(''); else setErr(errText(e));
          row.removeAttribute('disabled');
        }
      });
      return row;
    });
    accountsWrap.replaceChildren(
      h('p', { class: 'label', style: 'margin-bottom:8px' }, 'Accounts on this device'),
      h('div', { class: 'acct-list' }, ...rows),
      h('hr', { class: 'divider' }),
    );
  }

  const supported = webauthnSupported();
  const platform = supported && (await platformAuthenticatorAvailable());

  // "Use a join code" / "Recover the account" links. Fresh anchors are built per
  // section (buildJoinLinks) so each carries the typed username via ?u= and, for
  // recovery, opens the join page straight into recovery-key mode (?mode=recovery).
  const buildJoinLinks = (): { join: HTMLAnchorElement; recover: HTMLAnchorElement } => {
    const j = h('a', { href: '#/join' }, 'Use a join code') as HTMLAnchorElement;
    j.addEventListener('click', () => {
      const u = usernameInput.value.trim();
      j.setAttribute('href', u ? `#/join?u=${encodeURIComponent(u)}` : '#/join');
    });
    const r = h('a', { href: '#/join?mode=recovery' }, 'Recover the account') as HTMLAnchorElement;
    r.addEventListener('click', () => {
      const u = usernameInput.value.trim();
      r.setAttribute('href', u ? `#/join?mode=recovery&u=${encodeURIComponent(u)}` : '#/join?mode=recovery');
    });
    return { join: j, recover: r };
  };

  // "New device without a passkey? Use a join code or Recover the account."
  const newDeviceLine = (): HTMLElement => {
    const { join, recover } = buildJoinLinks();
    return h('p', { style: 'margin-top:14px' }, h('small', { class: 'muted' },
      'New device without a passkey?', h('br', {}), join, ' or ', recover, '.'));
  };

  // --- Reusable sections (order differs per experience) ---------------------

  // Create-account section: heading label + username field + Create button.
  const signupSection = (): HTMLElement[] => [
    h('p', { class: 'label', style: 'margin:0 0 4px' }, 'Create your VirtualID'),
    h('div', { class: 'field' },
      h('label', { class: 'label', for: 'username' }, 'Username'),
      usernameInput,
      h('small', { class: 'muted' }, '5–64 chars · letters, numbers, dots, hyphens, underscores · no email, no spaces'),
    ),
    signupBtn,
    platform ? h('p', { class: 'muted', style: 'margin-top:10px' }, h('small', {}, 'Tip: your device supports Face ID / Touch ID / Windows Hello — your passkey is protected by it.')) : null,
  ].filter(Boolean) as HTMLElement[];

  // Sign-in section: the discoverable-passkey button + helper caption.
  const signinSection = (): HTMLElement[] => (supported ? [
    // In signup-first the button is auto-width (narrower); center it in the card.
    signupFirst
      ? h('div', { style: 'display:flex;justify-content:center' }, passkeyBtn)
      : passkeyBtn,
    h('p', { class: 'muted', style: 'text-align:center;margin:8px 0 0' },
      h('small', {}, 'Choose your account in the popup — no username needed.')),
  ] : []);

  let card: HTMLElement;
  if (signupFirst) {
    // ===== NEW EXPERIENCE: prioritize account creation ======================
    card = h('div', { class: 'card', style: 'max-width:420px;width:100%' },
      h('div', { class: 'brand', style: 'font-size:1.3rem;margin-bottom:6px' },
        brandLogo(28), 'VirtualID'),
      h('h1', { style: 'font-size:1.5rem' }, 'One Identity, One You.'),
      h('p', { class: 'muted' }, 'We discovered you might not have an account yet! Start by creating one — it’s passwordless and takes a few seconds.'),
      !supported ? h('p', { class: 'badge badge-warn' }, 'This browser does not support passkeys (WebAuthn).') : null,

      // 1) Create account FIRST (primary).
      ...signupSection(),

      // 2) Existing user? -> passkey sign-in below.
      supported ? h('div', { class: 'or-sep' }, h('span', {}, 'existing user?')) : null,
      ...signinSection(),

      status,
      // 3) New device / recovery line.
      newDeviceLine(),
    );
  } else {
    // ===== CURRENT EXPERIENCE: sign-in first (unchanged) ====================
    card = h('div', { class: 'card', style: 'max-width:420px;width:100%' },
      h('div', { class: 'brand', style: 'font-size:1.3rem;margin-bottom:6px' },
        brandLogo(28), 'VirtualID'),
      h('h1', { style: 'font-size:1.5rem' }, 'One Identity, One You.'),
      h('p', { class: 'muted' }, 'Passwordless — no passwords to remember. Sign in with a passkey, or create your VirtualID below.'),
      !supported ? h('p', { class: 'badge badge-warn' }, 'This browser does not support passkeys (WebAuthn).') : null,
      accountsWrap,

      // Sign in (returning users) first.
      ...signinSection(),

      // Divider, then create-account section.
      supported ? h('div', { class: 'or-sep' }, h('span', {}, 'new here?')) : null,
      ...signupSection(),

      status,
      // New device / recovery line (now also includes "Recover the account").
      newDeviceLine(),
    );
  }

  app.replaceChildren(h('div', {
    style: 'min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;background:var(--bg-soft)',
  }, card));

  // Only the current (sign-in-first) experience shows the on-device accounts list.
  if (!signupFirst) renderAccounts();

  // NOTE: We deliberately do NOT start a background conditional-UI (autofill)
  // discoverable login here, and the signup username field does not carry the
  // `webauthn` autocomplete token. On desktop that combination popped the OS
  // passkey picker as soon as the user focused the "create a new username" field,
  // and cancelling it wedged the ceremony. The passkey picker now appears ONLY
  // when the user explicitly clicks "Sign in with passkey" (passkeyBtn).
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'JOIN_NOT_APPROVED') return 'Waiting for approval on your trusted device.';
    return `${e.message}`;
  }
  // WebAuthn focus requirement — surface an actionable hint rather than the raw
  // "The document is not focused." string (common when the tab lost focus).
  if (e instanceof DOMException && e.name === 'NotAllowedError' && /not focused/i.test(e.message)) {
    return 'This browser tab needs focus to create a passkey. Click anywhere on the page, then tap Create account again.';
  }
  if (e instanceof Error && e.name === 'DocumentNotFocusedError') return e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

// Flow C — cross-device join (new device enters a 6-digit code). Simplified per AUTH-FLOWS.
export async function renderJoin(_params?: Record<string, string>, query?: URLSearchParams): Promise<void> {
  const app = document.getElementById('app')!;
  const status = h('p', { class: 'muted' });
  const setErr = (msg: string) => { status.textContent = msg; status.classList.add('status-error'); status.setAttribute('role', 'alert'); };
  const userInput = h('input', { class: 'input', placeholder: 'your-username' }) as HTMLInputElement;
  // Prefill the username carried over from the login screen (?u=…) so the user
  // doesn't retype it. They can still edit it here.
  const prefillUser = query?.get('u')?.trim();
  if (prefillUser) userInput.value = prefillUser;
  const codeInput = h('input', { class: 'input', inputmode: 'numeric', maxlength: '6', placeholder: '6-digit code' }) as HTMLInputElement;
  // Recovery-key mode inputs (hidden until that mode is active).
  const recoveryInput = h('input', { class: 'input', style: 'font-family:ui-monospace,SFMono-Regular,Menlo,monospace', placeholder: 'recovery key' }) as HTMLInputElement;
  const totpInput = h('input', { class: 'input', inputmode: 'numeric', maxlength: '6', placeholder: '6-digit authenticator code' }) as HTMLInputElement;
  const totpField = h('div', { class: 'field', style: 'display:none' },
    h('label', { class: 'label' }, 'Authenticator code'), totpInput);
  const permanent = h('input', { type: 'checkbox', id: 'perm' }) as HTMLInputElement;

  // --- Mode toggle: "Join code" | "use recovery key" -------------------------
  type Mode = 'code' | 'recovery';
  // Deep-link: ?mode=recovery opens this page directly in recovery-key mode
  // (used by the "Recover the account" link on the login screen).
  let mode: Mode = query?.get('mode') === 'recovery' ? 'recovery' : 'code';
  const codeTab = h('span', { style: 'cursor:pointer' }, 'Join code') as HTMLSpanElement;
  const recoveryTab = h('span', { style: 'cursor:pointer;font-size:.82rem;color:var(--muted)' }, 'use recovery key') as HTMLSpanElement;
  const inputField = h('div', { class: 'field' },
    h('div', { class: 'row', style: 'gap:10px;align-items:baseline;margin-bottom:6px' }, codeTab, h('span', { class: 'muted' }, '/'), recoveryTab),
    codeInput, recoveryInput);
  function applyMode(): void {
    codeTab.style.fontWeight = mode === 'code' ? '700' : '400';
    recoveryTab.style.fontWeight = mode === 'recovery' ? '700' : '400';
    codeInput.style.display = mode === 'code' ? '' : 'none';
    recoveryInput.style.display = mode === 'recovery' ? '' : 'none';
    if (mode === 'code') totpField.style.display = 'none';
    status.textContent = '';
  }
  codeTab.addEventListener('click', () => { mode = 'code'; applyMode(); });
  recoveryTab.addEventListener('click', () => { mode = 'recovery'; applyMode(); });
  applyMode();

  const submit = h('button', { class: 'btn btn-primary btn-block' }, 'Continue');
  submit.addEventListener('click', async () => {
    const username = userInput.value.trim();
    if (mode === 'recovery') {
      const recoveryKey = recoveryInput.value.trim();
      if (!username || !recoveryKey) { setErr('Enter your username and recovery key.'); return; }
      const totpCode = totpInput.value.trim() || undefined;
      submit.setAttribute('disabled', 'true');
      try {
        await createDPoPKeypair(); // proof binds the recovered session to this device's key
        // A) Login occurs via the RECOVERY KEY credential (api.auth.recover),
        //    NEVER via a passkey assertion (no webauthn GET). Do not change this.
        const success = await api.auth.recover(username, recoveryKey, totpCode);
        session.set(success);
        rememberAccount({ username, display: username });
        toast('Signed in', 'ok');
        try { await ensureSubscriberKeypair(); } catch { /* non-fatal */ }
        // B/C/D) Optional best-effort "create a passkey on THIS device" step,
        //    gated on the SHARED 'permanent' checkbox AND existing_passkeys===0.
        //    The user is ALREADY logged in; this never logs them out or blocks,
        //    and always routes to the SAME destination ('/'). Mirrors the join
        //    flow via the shared enrollPasskeyBestEffort helper. Uses the authed
        //    self-register-begin (/me/passkeys/begin) + register finish
        //    (/me/passkeys/finish) — a WebAuthn CREATE, never an assertion.
        await enrollPasskeyBestEffort({
          beginFn: recoveryEnrollBegin,
          finishFn: (ceremonyId, credential) => api.auth.addPasskeyFinishCeremony(ceremonyId, credential, 'This device'),
          checkboxChecked: permanent.checked,
          destination: () => { router.navigate('/'); },
        });
      } catch (e) {
        if (e instanceof ApiError && e.code === 'TOTP_REQUIRED') {
          totpField.style.display = '';
          setErr('Enter your authenticator code.');
        } else {
          setErr('Recovery failed. Check your username and recovery key.');
        }
        submit.removeAttribute('disabled');
      }
      return;
    }
    const code = codeInput.value.trim();
    if (!username || code.length !== 6) { setErr('Enter your username and the 6-digit code.'); return; }
    submit.setAttribute('disabled', 'true');
    const persistDevice = permanent.checked;
    try {
      const res = await api.auth.joinRedeem(username, code);
      // Redeem succeeded -> awaiting_approval. Switch the card to the live
      // "Waiting on approval…" screen and start the poll/countdown loop.
      enterWaiting(res.join_id, username, persistDevice);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not redeem the join code.');
      submit.removeAttribute('disabled');
    }
  });

  // --- Waiting-on-approval screen -------------------------------------------
  // All timers registered here are cleared on teardown (route change / re-render)
  // so nothing leaks or double-fires. finishing guards against a double finish.
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let redirectTimer: ReturnType<typeof setTimeout> | null = null;
  let finishing = false;

  function clearTimers(): void {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
    if (redirectTimer) { clearTimeout(redirectTimer); redirectTimer = null; }
  }

  // Stop everything when we navigate away from #/join (back link, redirect, or
  // any other route change). One-shot: it removes itself after firing.
  function onHashChange(): void {
    if (!location.hash.startsWith('#/join')) {
      clearTimers();
      window.removeEventListener('hashchange', onHashChange);
    }
  }
  window.addEventListener('hashchange', onHashChange);

  function scheduleLoginRedirect(): void {
    if (redirectTimer) return;
    redirectTimer = setTimeout(() => { router.navigate('/login'); }, JOIN_REDIRECT_SECONDS * 1000);
  }

  function enterWaiting(joinID: string, username: string, persistDevice: boolean): void {
    let secondsLeft = JOIN_WAIT_SECONDS;

    const heading = h('h1', { style: 'font-size:1.4rem' }, 'Waiting on approval…');
    const subtitle = h('p', { class: 'muted' },
      'Approve this device on a machine where you are already signed in.'
      + (persistDevice ? ' Once approved, this device will register a passkey and stay trusted.' : ' Once approved, you will be signed in for this session only.'));
    const countdown = h('div', { style: 'font-size:2.2rem;font-weight:700;text-align:center;margin:18px 0' }, `${secondsLeft}s`);
    const waitStatus = h('p', { class: 'muted', style: 'text-align:center;min-height:1.2em', 'aria-live': 'polite' });
    const waitCard = h('div', { class: 'card', style: 'max-width:420px;width:100%' },
      heading, subtitle, countdown, waitStatus,
      h('p', { style: 'margin-top:12px' }, h('a', { href: '#/login' }, '← Back to sign in')),
    );
    app.replaceChildren(h('div', { style: 'min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;background:var(--bg-soft)' }, waitCard));

    const setTerminal = (msg: string) => { waitStatus.textContent = msg; };

    async function completeApprovedLogin(): Promise<void> {
      if (finishing) return;
      finishing = true;
      clearTimers();
      setTerminal('Approved ✓ — signing you in…');
      try {
        await createDPoPKeypair(); // bind the new session to this device's DPoP key

        // Tracks whether a DPoP-bound session has been issued for this device yet.
        // A) Login here ALWAYS happens via the JOIN credential (api.auth.joinFinish),
        //    NEVER via a passkey assertion (no webauthn GET). joinFinish(false)
        //    issues an ephemeral session; joinFinish(true,...) registers the new
        //    passkey AND issues the session in one call. Do not substitute a
        //    passkey login here.
        let loggedIn = false;
        const ensureLoggedIn = async (): Promise<void> => {
          if (loggedIn) return;
          const t = await api.auth.joinFinish(joinID, false); // ephemeral session, no passkey
          session.set(t);
          loggedIn = true;
        };

        const proceed = async (): Promise<void> => {
          await ensureLoggedIn(); // guarantee a session even if enroll was skipped
          try { await ensureSubscriberKeypair(); } catch { /* non-fatal */ }
          window.removeEventListener('hashchange', onHashChange);
          router.navigate('/'); // D) SAME destination for success + skip + failure
        };

        // B/C/D) Shared best-effort enroll, IDENTICAL to the recovery flow. Gated
        //    on the SHARED 'permanent' checkbox (persistDevice) AND
        //    existing_passkeys===0. On success the passkey is registered via
        //    joinFinish(true,...) (which also issues the session). On skip or ANY
        //    failure the user is still logged in (ephemeral) and routed to '/'.
        await enrollPasskeyBestEffort({
          beginFn: () => api.auth.joinRegisterBegin(joinID),
          finishFn: async (ceremonyId, credential) => {
            // join's register-finish ALSO issues the session (permanent device).
            const tokens = await api.auth.joinFinish(joinID, true, ceremonyId, credential);
            session.set(tokens);
            loggedIn = true;
            rememberAccount({ username, display: username });
          },
          checkboxChecked: persistDevice,
          destination: proceed,
        });
      } catch (e) {
        finishing = false;
        setTerminal(errText(e));
        scheduleLoginRedirect();
      }
    }

    async function poll(): Promise<void> {
      if (finishing) return;
      let status = 'awaiting_approval';
      try {
        const res = await api.auth.joinStatus(joinID);
        status = res.status;
      } catch {
        // Network blip: keep waiting; the local countdown still governs timeout.
      }
      const action = nextAction(status, secondsLeft);
      if (!isTerminal(action)) return;
      clearTimers();
      if (action === 'login') { void completeApprovedLogin(); return; }
      // denied or timeout: show the message, then auto-redirect to login.
      setTerminal(terminalMessage(action));
      scheduleLoginRedirect();
    }

    countdownTimer = setInterval(() => {
      secondsLeft = tickCountdown(secondsLeft);
      countdown.textContent = `${secondsLeft}s`;
      if (secondsLeft <= 0) {
        clearTimers();
        if (!finishing) { setTerminal(terminalMessage('timeout')); scheduleLoginRedirect(); }
      }
    }, 1000);
    pollTimer = setInterval(() => { void poll(); }, 1500);
    void poll(); // immediate first check
  }

  const card = h('div', { class: 'card', style: 'max-width:420px;width:100%' },
    h('h1', { style: 'font-size:1.4rem' }, 'Log in on a new device'),
    h('p', { class: 'muted' }, 'Enter the 6-digit join code from a device where you are already logged in.'),
    h('div', { class: 'field' }, h('label', { class: 'label' }, 'Username'), userInput),
    inputField,
    totpField,
    h('div', { class: 'row', style: 'gap:8px;margin-bottom:14px;align-items:flex-start;flex-wrap:nowrap' },
      h('div', { style: 'flex:0 0 auto;padding-top:2px' }, permanent),
      h('label', { for: 'perm', style: 'color:var(--danger);font-weight:600;font-size:.85rem' }, 'This is a permanent device — trust it for future logins and create passkey on this device.')),
    submit,
    status,
    h('p', { style: 'margin-top:12px' }, h('a', { href: '#/login' }, '← Back to sign in')),
  );
  app.replaceChildren(h('div', { style: 'min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;background:var(--bg-soft)' }, card));
}
